import { Badge, Box, HStack, Icon, Link, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FiCheckCircle, FiClock, FiExternalLink, FiFileText, FiLock, FiShield } from "react-icons/fi";
import { BlockchainInfoData, getBlockchainInfo } from "@lubbezposrednio-frontend/core";

/** Stan zakotwiczenia głosowania na blockchainie — wspólny dla widoku głosowania i zarządzania.
 *  Jawne: create_voting per pytanie („głosowanie utworzone") + anchor wyniku. Tajne: ochrona ZKP + werdykt.
 *  Publicznie chowa się, gdy nic jeszcze nie zakotwiczone; w zarządzaniu (showPending) pokazuje też stan oczekujący. */
export const BlockchainInfo: React.FC<{ votingId: string; showPending?: boolean; bare?: boolean }> = ({
  votingId,
  showPending = false,
  bare = false
}) => {
  const [info, setInfo] = useState<BlockchainInfoData | null>(null);

  useEffect(() => {
    let active = true;
    getBlockchainInfo(votingId)
      .then(r => active && setInfo(r.data))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [votingId]);

  if (!info) return null;

  const qText = (q: { question: string | null }) =>
    (q.question || "Pytanie").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || "Pytanie";

  const anchoredQuestions = info.questions.filter(q => q.onchain_voting_id);
  const pendingQuestions = info.questions.filter(q => !q.onchain_voting_id);
  const hasResult = !!info.result.txid;
  const hasAny = info.anchored || hasResult || (info.secret && info.finished);

  if (!hasAny && !showPending) return null;

  const card = (children: React.ReactNode, footer?: React.ReactNode) => {
    const inner = (
      <Box
        bg="white"
        border="1px solid rgba(67,0,152,0.10)"
        borderRadius="16px"
        boxShadow="0 6px 20px rgba(67,0,152,0.05)"
        p={{ base: "16px", md: "20px" }}
      >
        <HStack spacing="9px" mb="4px">
          <Icon as={FiShield} color="primary" boxSize="18px" />
          <Text fontWeight="800" color="primary">
            Blockchain
          </Text>
          {info.anchored || hasResult ? (
            <Badge colorScheme="green" borderRadius="full" px="9px" fontSize="10px">
              Zakotwiczone
            </Badge>
          ) : (
            <Badge colorScheme="purple" borderRadius="full" px="9px" fontSize="10px">
              Oczekuje
            </Badge>
          )}
        </HStack>
        {children}
        {footer}
      </Box>
    );
    return bare ? (
      inner
    ) : (
      <Box w="100%" maxW="720px" mx="auto" px={{ base: "16px", md: "20px" }} pt="16px">
        {inner}
      </Box>
    );
  };

  // Tajne — chronione ZKP
  if (info.secret) {
    return card(
      <>
        <HStack spacing="9px" align="flex-start" mt="6px">
          <Icon as={FiLock} color="gray.400" boxSize="15px" mt="2px" />
          <Text fontSize="13px" color="gray.600">
            Tajne głosowanie — głosy są anonimowe i chronione dowodami zerowej wiedzy (ZKP). Wynik jest liczony bezpośrednio
            z łańcucha, bez ujawniania, kto jak zagłosował.
          </Text>
        </HStack>
        {info.result.verification_url && (
          <Link href={info.result.verification_url} isExternal color="primary" fontWeight="700" fontSize="13px" mt="12px" display="inline-flex">
            <HStack spacing="5px">
              <Text>Zweryfikuj na blockchainie</Text>
              <Icon as={FiExternalLink} boxSize="13px" />
            </HStack>
          </Link>
        )}
      </>
    );
  }

  // Jawne
  return card(
    <>
      <Text fontSize="13px" color="gray.500" mt="2px" mb="14px">
        To głosowanie jest zapisane na blockchainie — każde pytanie zostało utworzone jako osobna transakcja, którą każdy
        może niezależnie sprawdzić w eksploratorze.
      </Text>

      <VStack align="stretch" spacing="10px">
        {anchoredQuestions.map(q => (
          <Box key={q.id} border="1px solid rgba(67,0,152,0.08)" borderRadius="12px" p="12px 14px">
            <HStack justify="space-between" align="center" gap="10px">
              <HStack spacing="8px" minW={0}>
                <Icon as={FiCheckCircle} color="green.500" boxSize="15px" flexShrink={0} />
                <Text fontSize="13px" fontWeight="700" color="gray.700" noOfLines={1}>
                  Pytanie: {qText(q)}
                </Text>
              </HStack>
              {q.explorer_url && (
                <Link href={q.explorer_url} isExternal color="primary" fontWeight="700" fontSize="13px" flexShrink={0}>
                  <HStack spacing="4px">
                    <Text>Eksplorer</Text>
                    <Icon as={FiExternalLink} boxSize="13px" />
                  </HStack>
                </Link>
              )}
            </HStack>
            {q.onchain_voting_id && (
              <Text fontFamily="mono" fontSize="11px" color="gray.400" mt="4px" noOfLines={1}>
                {q.onchain_voting_id}
              </Text>
            )}
          </Box>
        ))}

        {showPending &&
          pendingQuestions.map(q => (
            <HStack key={q.id} spacing="8px" border="1px dashed rgba(67,0,152,0.14)" borderRadius="12px" p="10px 14px">
              <Icon as={FiClock} color="gray.400" boxSize="14px" flexShrink={0} />
              <Text fontSize="12.5px" color="gray.500" noOfLines={1}>
                Pytanie: {qText(q)} — oczekuje na zakotwiczenie
              </Text>
            </HStack>
          ))}
      </VStack>

      {(info.result.verification_url || info.result.explorer_url || info.result.protocol_url) && (
        <HStack spacing="16px" mt="14px" pt="12px" borderTop="1px solid rgba(67,0,152,0.06)" wrap="wrap">
          {info.result.txid && info.result.verification_url && (
            <Link href={info.result.verification_url} isExternal color="primary" fontWeight="700" fontSize="13px">
              <HStack spacing="5px">
                <Icon as={FiShield} boxSize="13px" />
                <Text>Werdykt wyniku</Text>
              </HStack>
            </Link>
          )}
          {info.result.protocol_url && (
            <Link href={info.result.protocol_url} isExternal color="primary" fontWeight="700" fontSize="13px">
              <HStack spacing="5px">
                <Icon as={FiFileText} boxSize="13px" />
                <Text>Protokół PDF</Text>
              </HStack>
            </Link>
          )}
        </HStack>
      )}

      {!info.result.txid && (
        <Text fontSize="11.5px" color="gray.400" mt="12px">
          Zbiorczy wynik zostanie dodatkowo zakotwiczony po zakończeniu głosowania.
        </Text>
      )}
    </>
  );
};
